export type Component = {
  code: string;
  name: string;
  unit: string;
  perUnit: number;
};

export type PlannedDay = {
  key: string;
  label: string;
  quantity: number;
};

export type TreeLine = {
  parent: string;
  code: string;
  name: string;
  unit: string;
  level: number;
  directRatio: number;
  cumulativePerUnit: number;
  path: string;
};

export type Product = {
  code: string;
  name: string;
  unit: string;
  planQuantity: number;
  sourceRows: number[];
  workOrders: string[];
  lineNames: string[];
  dailyPlan: PlannedDay[];
  components: Component[];
  tree: TreeLine[];
};

export type Sarf39Snapshot = {
  source: {
    plan: string;
    planSheet: string;
    mikroSnapshot: string;
    note: string;
  };
  scope: { productPrefix: string; calculation: string };
  products: Product[];
};

export type TreeNode = TreeLine & { children: TreeNode[] };

export const numberFormat = new Intl.NumberFormat("tr-TR", {
  maximumFractionDigits: 6,
});

export const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const weekdayFormatter = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Istanbul", weekday: "long" });

export function scheduledQuantity(product: Product) {
  const total = product.dailyPlan.reduce((sum, day) => sum + day.quantity, 0);
  return total || product.planQuantity;
}

export function plannedQuantityForDay(product: Product, dayKey: string) {
  return product.dailyPlan.find((day) => day.key === dayKey)?.quantity ?? 0;
}

export function currentPlanDayKey(products: Product[]) {
  const dayKey = weekdayFormatter.format(new Date()).toLocaleLowerCase("en-US");
  return products.some((product) => plannedQuantityForDay(product, dayKey) > 0) ? dayKey : null;
}

export function buildTree(product: Product) {
  const root: { children: TreeNode[] } = { children: [] };
  const nodes = new Map<string, { children: TreeNode[] }>([
    [`>${product.code}>`, root],
  ]);

  for (const line of product.tree) {
    const parts = line.path.split(">").filter(Boolean);
    const parentPath = `>${parts.slice(0, -1).join(">")}>`;
    const parent = nodes.get(parentPath) ?? root;
    const node: TreeNode = { ...line, children: [] };
    parent.children.push(node);
    nodes.set(line.path, node);
  }

  return root.children;
}

